// CallerIdDropdown — caller-ID picker attached to the CLICK TO CALL button on
// the Lead Detail left column. Lists the tenant's provisioned phone numbers
// (GET /settings/phone-numbers, tenant-scoped server-side) so the agent can
// choose which local number to present. Selection is lifted to the parent,
// which passes it to the click-to-call request.

import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, Check, Phone } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { apiGet } from '../../lib/api.js';
import { fmtPhone } from '../../lib/format.js';

export default function CallerIdDropdown({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const { data } = useQuery({
    queryKey: ['tenant-phone-numbers'],
    queryFn: () => apiGet('/settings/phone-numbers'),
    staleTime: 60_000,
  });
  const numbers = data?.numbers || [];

  useEffect(() => {
    function onOutside(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', onOutside);
    return () => document.removeEventListener('mousedown', onOutside);
  }, []);

  // Default to the primary number once the list loads.
  useEffect(() => {
    if (!value && numbers.length) {
      const primary = numbers.find((n) => n.isPrimary) || numbers[0];
      onChange?.(primary.phoneNumber);
    }
  }, [numbers, value, onChange]);

  const label = value ? fmtPhone(value) : 'Caller ID';

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label="Select caller ID"
        className="flex items-center gap-1.5 px-2 py-1.5 rounded-lg bg-civix-panel
                   border border-civix-border text-[11px] text-white/70
                   hover:border-civix-purple/60 transition"
      >
        <Phone className="w-3 h-3" />
        <span className="truncate max-w-[110px]">{label}</span>
        <ChevronDown className="w-3 h-3 shrink-0" />
      </button>

      {open && (
        <ul
          role="listbox"
          className="absolute z-30 mt-1 right-0 w-48 max-h-60 overflow-y-auto rounded-lg
                     bg-civix-panel border border-civix-border shadow-xl py-1"
        >
          {numbers.length === 0 && (
            <li className="px-3 py-2 text-[11px] text-white/40">No numbers provisioned</li>
          )}
          {numbers.map((n) => (
            <li key={n._id} role="option" aria-selected={value === n.phoneNumber}>
              <button
                type="button"
                onClick={() => { onChange?.(n.phoneNumber); setOpen(false); }}
                className="w-full flex items-center justify-between gap-2 px-3 py-1.5 text-left
                           text-[11px] text-white/75 hover:bg-civix-purple/15"
              >
                <span className="truncate">
                  {fmtPhone(n.phoneNumber)}
                  {n.isPrimary && <span className="ml-1 text-civix-cyan">(primary)</span>}
                </span>
                {value === n.phoneNumber && <Check className="w-3 h-3 text-civix-cyan shrink-0" />}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
